import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/** Decimals — place value extended to the right of the ones. */
export const DECIMAL_LESSONS: Lesson[] = [
  lesson({
    id: 'decimal-place-value-01',
    unitId: 'unit-decimals',
    conceptId: 'decimal-place-value',
    title: 'Decimal Place Value',
    summary: 'Place value does not stop at the ones. Keep going right and you get tenths, hundredths and thousandths.',
    objectives: ['Name the value of digits after the decimal point', 'Write decimals as fractions', 'Read decimals correctly aloud'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Past the ones',
        blocks: [
          text('Going left along a number, each place is worth ten times more. It follows that going right, each place is worth ten times less.'),
          text('The ones place is not the end of the line. Carry on: tenths, hundredths, thousandths. The decimal point simply marks where the ones stop.'),
          math('0.4 = \\frac{4}{10} \\qquad 0.07 = \\frac{7}{100}'),
        ],
      },
      intuition: {
        title: 'Decimals are fractions in disguise',
        blocks: [
          text('A decimal is a fraction whose denominator is 10, or 100, or 1000 — a fraction restricted to the sizes our number system prefers.'),
          text('That restriction is what makes decimals easy to compute with and easy to compare, and it is why money and measurement use them.'),
          insight('0.5 and 1/2 are the same number written in two notations. Neither is more real than the other.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'place-value', digits: '325', places: ['Ones', 'Tenths', 'Hundredths'] }),
          table(['Decimal', 'Fraction', 'In words'], [['0.3', '3/10', 'three tenths'], ['0.03', '3/100', 'three hundredths'], ['1.25', '1 25/100', 'one and twenty-five hundredths']]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('What is the value of the 6 in 4.263?'),
          steps([
            { label: 'Name the places after the point.', latex: '2 \\text{ tenths}, \; 6 \\text{ hundredths}, \; 3 \\text{ thousandths}' },
            { label: 'The 6 sits in the hundredths place.', latex: '\\frac{6}{100} = 0.06' },
          ]),
          warning('Read it as "four point two six three", not "four point two hundred and sixty-three". The digits after the point are read one at a time, because each has its own place.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('decimal-place-value', 4)] },
      independent: { title: 'On your own', blocks: [practice('decimal-place-value', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('decimal-place-value', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Each place right of the point is ten times smaller.', 'Tenths, hundredths, thousandths.', 'Every decimal is a fraction over a power of ten.'])],
      },
    },
  }),

  lesson({
    id: 'compare-decimals-01',
    unitId: 'unit-decimals',
    conceptId: 'compare-decimals',
    title: 'Comparing Decimals',
    summary: 'Compare place by place from the left. More digits does not mean a bigger number.',
    objectives: ['Compare two decimals', 'Order a list of decimals', 'Explain why 0.7 is larger than 0.25'],
    estimatedMinutes: 20,
    sections: {
      introduction: {
        title: 'The trap',
        blocks: [
          text('Which is bigger: 0.7 or 0.25?'),
          text('25 is bigger than 7, so many people say 0.25. But 0.7 is seven tenths and 0.25 is only two tenths and a bit. 0.7 wins comfortably.'),
          warning('With whole numbers, more digits does mean bigger. After the decimal point, that rule stops working entirely.'),
        ],
      },
      intuition: {
        title: 'Compare like with like',
        blocks: [
          text('Write 0.7 as 0.70. Now both numbers have hundredths, and comparing 70 hundredths with 25 hundredths is easy.'),
          math('0.70 > 0.25'),
          insight('Adding a zero to the end of a decimal never changes its value — it just renames the pieces, exactly as with equivalent fractions.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [figure({ kind: 'number-line', from: 0, to: 1, step: 0.1, marks: [{ at: 0.25, label: '0.25' }, { at: 0.7, label: '0.7' }] }, 'On the number line there is no argument.')],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Order 3.4, 3.09 and 3.401 from smallest to largest.'),
          steps([
            { label: 'Give them all the same number of decimal places.', latex: '3.400, \; 3.090, \; 3.401' },
            { label: 'Compare the ones — all equal.', latex: '3 = 3 = 3' },
            { label: 'Compare the tenths.', latex: '0 < 4', note: '3.09 is the smallest.' },
            { label: 'The other two agree to the hundredths, so compare thousandths.', latex: '3.400 < 3.401' },
            { label: 'Answer.', latex: '3.09 < 3.4 < 3.401' },
          ]),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('compare-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('compare-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('compare-decimals', 3)] },
      review: {
        title: 'What to remember',
        blocks: [list(['Compare place by place, starting from the left.', 'Pad with zeros so both numbers have the same number of places.', 'Length is not size.'])],
      },
    },
  }),

  lesson({
    id: 'add-decimals-01',
    unitId: 'unit-decimals',
    conceptId: 'add-decimals',
    title: 'Adding and Subtracting Decimals',
    summary: 'Line up the decimal points and it becomes ordinary column arithmetic.',
    objectives: ['Add and subtract decimals in columns', 'Use zeros to fill missing places', 'Estimate to check the result'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'One rule',
        blocks: [
          text('Adding decimals uses everything you already know about adding whole numbers. There is exactly one new rule: line up the decimal points.'),
          text('That rule is not cosmetic. It is what keeps tenths above tenths and hundredths above hundredths — the same place-value alignment that column addition has always required.'),
        ],
      },
      intuition: {
        title: 'Why alignment matters',
        blocks: [
          text('Adding 3.5 and 0.42 by lining up the right-hand edges would put the 5 tenths above the 2 hundredths. That is adding a metre to a centimetre and calling the answer 7.'),
          math('3.50 + 0.42 = 3.92'),
          tip('Write in the missing zeros. 3.5 becomes 3.50, and suddenly both numbers have the same shape.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [table(['Wrong', 'Right'], [['3.5 + 0.42 aligned right', '3.50 + 0.42 aligned on the point']])],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Compute 12.4 + 3.75.'),
          steps([
            { label: 'Pad so both have two decimal places.', latex: '12.40 + 3.75' },
            { label: 'Hundredths.', latex: '0 + 5 = 5' },
            { label: 'Tenths.', latex: '4 + 7 = 11', note: 'Write 1, carry 1 into the ones.' },
            { label: 'Ones and tens, with the carry.', latex: '12 + 3 + 1 = 16' },
            { label: 'Answer.', latex: '12.4 + 3.75 = 16.15' },
          ]),
          tip('Estimate: 12 + 4 = 16. The answer 16.15 sits right where it should.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('add-decimals', 4)] },
      independent: { title: 'On your own', blocks: [practice('add-decimals', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('add-decimals', 3, 'Money problems, where the answer must make sense in pounds and pence.')] },
      review: {
        title: 'What to remember',
        blocks: [list(['Line up the decimal points.', 'Fill empty places with zeros.', 'Carry and regroup exactly as with whole numbers.', 'The decimal point in the answer sits below the others.'])],
      },
    },
  }),
];
